import { useState, useCallback } from 'react';
import { DecisionInput } from './components/DecisionInput';
import { AssessmentView } from './components/AssessmentView';
import { ResultsView } from './components/ResultsView';
import ToolWrapper, { type ToolHostProps, type ToolResultItem } from '../../components/prioritization/ToolWrapper';
import { type ToolItem } from '../../contexts/toolItemsHooks';
import { useLiveToolSession } from '../../components/prioritization/session/liveExperience/useLiveToolSession';
import { LiveToolScaffold } from '../../components/prioritization/session/liveExperience/LiveToolScaffold';
import { FastVsRightLive } from '../../components/prioritization/session/liveExperience/liveTools/FastVsRightLive';
import type { DecisionData } from './types';

type AppState = 'input' | 'assessing' | 'results';

function FastVsRightContent(props: ToolHostProps) {
  const { isLiveSession, shouldAutoStart } = useLiveToolSession();
  const [state, setState] = useState<AppState>('input');
  const [decisionData, setDecisionData] = useState<DecisionData | null>(null);
  const [urgencyLevel, setUrgencyLevel] = useState<'low' | 'medium' | 'high' | null>(null);
  const [correctnessLevel, setCorrectnessLevel] = useState<'low' | 'medium' | 'high' | null>(null);

  const handleStart = (data: DecisionData) => {
    setDecisionData(data);
    setState('assessing');
  };

  const handleUrgencySelect = (level: 'low' | 'medium' | 'high') => {
    setUrgencyLevel(level);
  };

  const handleCorrectnessSelect = (level: 'low' | 'medium' | 'high') => {
    setCorrectnessLevel(level);
    if (urgencyLevel && level && !isLiveSession) {
      setState('results');
    }
  };

  const handleGetResults = useCallback((): ToolResultItem[] => {
    if (!decisionData || !urgencyLevel || !correctnessLevel) return [];
    const quadrant =
      urgencyLevel === 'high' && correctnessLevel === 'low'
        ? 'fast-wrong'
        : urgencyLevel === 'low' && correctnessLevel === 'high'
          ? 'slow-right'
          : urgencyLevel === 'high' && correctnessLevel === 'high'
            ? 'invest'
            : 'defer';
    return [
      {
        id: 'decision',
        name: decisionData.decisionName,
        rank: 1,
        category: quadrant,
        classificationBreakdown: { urgency: urgencyLevel, correctness: correctnessLevel },
        metadata: { urgency: urgencyLevel, correctness: correctnessLevel, context: decisionData.context },
      },
    ];
  }, [decisionData, urgencyLevel, correctnessLevel]);

  const canSubmitLive = isLiveSession && !!urgencyLevel && !!correctnessLevel;

  const handleReset = () => {
    setState('input');
    setDecisionData(null);
    setUrgencyLevel(null);
    setCorrectnessLevel(null);
  };

  // Map the first imported item to a decision context and start the assessment
  const handleImportItems = useCallback((toolItems: ToolItem[]) => {
    if (toolItems.length === 0) return;
    const first = toolItems[0];
    const data: DecisionData = {
      decisionName: first.name,
      context: first.description || `Decision imported from workspace: ${first.name}`,
      successCriteria: 'To be defined during assessment',
    };
    setDecisionData(data);
    setState('assessing');
  }, []);

  return (
    <ToolWrapper
      toolName="Fast vs Right Framework"
      toolDescription="Determine whether a decision requires speed or rigor"
      onImportItems={handleImportItems}
      onGetResults={canSubmitLive || state === 'results' ? handleGetResults : undefined}
      itemCount={decisionData ? 1 : 0}
      {...props}
    >
      <div className="w-full">
        {/* Main Content */}
        <div>
          {state === 'input' && !shouldAutoStart && (
            <DecisionInput onStart={handleStart} />
          )}

          {state === 'assessing' && decisionData && isLiveSession && (
            <LiveToolScaffold
              title="Fast vs right"
              subtitle="How urgent is this decision, and how much does being right matter?"
            >
              <FastVsRightLive
                decisionName={decisionData.decisionName}
                context={decisionData.context}
                urgencyLevel={urgencyLevel}
                correctnessLevel={correctnessLevel}
                onUrgencySelect={handleUrgencySelect}
                onCorrectnessSelect={handleCorrectnessSelect}
              />
            </LiveToolScaffold>
          )}

          {state === 'assessing' && decisionData && !isLiveSession && (
            <AssessmentView
              decisionData={decisionData}
              urgencyLevel={urgencyLevel}
              correctnessLevel={correctnessLevel}
              onUrgencySelect={handleUrgencySelect}
              onCorrectnessSelect={handleCorrectnessSelect}
            />
          )}

          {state === 'results' && decisionData && urgencyLevel && correctnessLevel && (
            <ResultsView 
              decisionData={decisionData}
              urgencyLevel={urgencyLevel}
              correctnessLevel={correctnessLevel}
              onReset={handleReset}
            />
          )}
        </div>
      </div>
    </ToolWrapper>
  );
}

export default function FastVsRight(props: ToolHostProps) {
  return <FastVsRightContent {...props} />;
}


